import { useStable } from '@/hooks/useStable';
import { Device } from '@/types/app';
import { Component } from '@/types/component';
import { MobileAppEnvironment, MobileAppTrackingStatus } from '@/types/mobileApp';
import { PaymentConfig } from '@/types/payment';
import { AppState, AppStateProvider } from 'lib/contexts/AppState';
import { ComponentsStateProvider } from 'lib/contexts/ComponentsState';
import { MobileAppState, MobileAppStateProvider } from 'lib/contexts/MobileAppState';
import { useRouter } from 'next/router';

export interface StandaloneStateProvidersProps {
  device: Device;
  pageData: any;
  appState?: Partial<AppState>;
  mobileAppState?: Partial<MobileAppState>;
  paymentConfig?: PaymentConfig;
}

export const StandaloneStateProviders: Component<StandaloneStateProvidersProps> = ({ children, ...pageProps }) => {
  const { query, pathname } = useRouter();

  const { device, pageData, appState, mobileAppState, paymentConfig } = useStable(pageProps);

  const { type, data, meta } = pageData ?? {};
  const { id, isNativeAd, accessLevel, purchaseTag, pageType: metaPageType } = meta ?? {};
  const { publishedUrl, category, tags } = data ?? {};

  const baseAppState: AppState = useStable({
    device,
    type,
    category,
    publishedUrl,
    tags,
    pageId: id,
    isNativeAd,
    accessLevel,
    purchaseTag,
    metaPageType,
    gamesUrls: ['/korsord', '/korsord-och-spel'],
    isPreviewPage: pathname.startsWith('/_preview'),
    isFrontPage: type === 'front',
    isArticlePage: type === 'article',
    isDesktop: device === 'desktop',
    isMobile: device === 'mobile',
    recommendedArticles: [],
    scrollArticles: [],
    paymentConfig,
  });

  const baseMobileAppState: MobileAppState = useStable({
    isMobileApp: Boolean(query.allerapp),
    appVersion: query['app-version'] as string,
    buildVersion: query['build-version'] as string,
    environment: query.environment as MobileAppEnvironment,
    trackingStatus: query['tracking-status'] as MobileAppTrackingStatus,
  });

  return (
    <AppStateProvider {...baseAppState} {...appState}>
      <MobileAppStateProvider {...baseMobileAppState} {...mobileAppState}>
        <ComponentsStateProvider>{children}</ComponentsStateProvider>
      </MobileAppStateProvider>
    </AppStateProvider>
  );
};
